import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageContext";
import { ContentCourse, Module, Question } from "../../schemas/schemas";

// Import the new components
import { CourseHeader } from "./course-header";
import { CourseNavigation } from "./course-navigation";
import { ModuleCard } from "./module-card";
import { CourseSettings } from "./course-settings";
import { courseContentService } from "../../services/courseContentService";
import QuestionBankModal from "./question-bank-modal";
import QuestionModal from "./question-modal";

interface CourseComposerProps {
  courseId: string;
  itemId?: string;
  itemType?: string;
}

const defaultCourseData: ContentCourse = {
  id: "",
  name: "",
  title: "",
  description: "",
  status: "draft",
  modules: []
};

export default function CourseComposer({
  courseId,
  itemId,
  itemType,
}: CourseComposerProps) {
  const { t } = useLanguage();
  
  // For demonstration purposes
  const isLesson = itemType === "lesson";
  const isExercise = itemType === "exercise";

  // State for the course structure - now using standard ContentCourse schema
  const [courseData, setCourseData] = useState<ContentCourse>(defaultCourseData);
  const [questionBank, setQuestionBank] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);

  // State for unsaved changes dialog
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
  const [pendingNavigationCallback, setPendingNavigationCallback] = useState<(() => void) | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load course data from API - now returns proper ContentCourse schema
        const course = await courseContentService.getCourse(courseId);
        setCourseData(course);
        
        // Load question bank from API
        const questions = await courseContentService.getQuestionBank();
        setQuestionBank(questions);
        
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [courseId]);

  // State for question modal
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);

  // State for question bank modal
  const [isQuestionBankModalOpen, setIsQuestionBankModalOpen] = useState(false);
  const [currentActivityId, setCurrentActivityId] = useState<string | null>(null);

  // Get the current activity if one is selected
  const getCurrentActivity = () => {
    if (!currentActivityId) return null;

    for (const module of courseData.modules || []) {
      for (const section of module.sections || []) {
        for (const activity of section.activities || []) {
          if (activity.activity_id === currentActivityId && activity.type === 'exercise') {
            return {
              questions: activity.questions || []
            };
          }
        }
      }
    }
    return null;
  };

  // Handler for adding or updating a question
  const handleSaveQuestion = (question: Question) => {
    setCourseData((prevCourse) => {
      const updatedCourse = { ...prevCourse };

      for (const module of updatedCourse.modules || []) {
        for (const section of module.sections || []) {
          for (const activity of section.activities || []) {
            if (activity.type === 'exercise' && activity.questions) {
              if (editingQuestion) {
                // Update existing question
                activity.questions = activity.questions.map((q: Question) =>
                  q.question_id === question.question_id ? question : q
                );
              } else {
                // Add new question
                const newQuestion: Question = {
                  ...question,
                  question_id: `q${Date.now()}`,
                };
                activity.questions = [...activity.questions, newQuestion];
              }
            }
          }
        }
      }

      return updatedCourse;
    });

    setEditingQuestion(null);
  };

  // Handler for opening the modal to edit a question
  const handleEditQuestion = (question: Question) => {
    setEditingQuestion(question);
    setIsQuestionModalOpen(true);
  };

  // Handler for removing a question
  const handleRemoveQuestion = (questionId: string) => {
    setCourseData((prevCourse) => {
      const updatedCourse = { ...prevCourse };

      for (const module of updatedCourse.modules || []) {
        for (const section of module.sections || []) {
          for (const activity of section.activities || []) {
            if (activity.type === 'exercise' && activity.questions) {
              activity.questions = activity.questions.filter(
                (q: Question) => q.question_id !== questionId
              );
            }
          }
        }
      }

      return updatedCourse;
    });
  };

  // Handler for adding questions from question bank
  const handleAddQuestionsFromBank = (questions: Question[]) => {
    if (!currentActivityId) return;

    setCourseData((prevCourse) => {
      const updatedCourse = { ...prevCourse };

      for (const module of updatedCourse.modules || []) {
        for (const section of module.sections || []) {
          for (const activity of section.activities || []) {
            if (activity.activity_id === currentActivityId && activity.type === 'exercise') {
              const existingQuestionIds = activity.questions
                ? activity.questions.map((q: Question) => q.question_id)
                : [];
              
              const newQuestions: Question[] = questions
                .filter((q) => !existingQuestionIds.includes(q.question_id))
                .map(q => ({
                  ...q,
                  question_id: `q${Date.now()}_${q.question_id}`,
                }));

              activity.questions = activity.questions
                ? [...activity.questions, ...newQuestions]
                : [...newQuestions];
            }
          }
        }
      }

      return updatedCourse;
    });
  };

  // Handler for opening the question bank modal
  const handleOpenQuestionBank = (activityId: string) => {
    setCurrentActivityId(activityId);
    setIsQuestionBankModalOpen(true);
  };

  // Handler for updating activity content (for lessons and other content-based activities)
  const handleActivityContentChange = (activityId: string, newContent: string) => {
    setCourseData((prevCourse) => {
      const updatedCourse = { ...prevCourse };

      for (const module of updatedCourse.modules || []) {
        for (const section of module.sections || []) {
          for (const activity of section.activities || []) {
            if (activity.activity_id === activityId && activity.type !== 'exercise') {
              // Store content in a custom property since it's not in the standard schema
              (activity as any).content_data = newContent;
            }
          }
        }
      }

      return updatedCourse;
    });
  };

  // Handle discarding changes and navigate away
  const handleDiscardChanges = () => {
    setIsUnsavedChangesDialogOpen(false);
    if (pendingNavigationCallback) {
      pendingNavigationCallback();
      setPendingNavigationCallback(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading course data...</div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      {/* Question modal */}
      <QuestionModal
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setEditingQuestion(null);
        }}
        onSave={handleSaveQuestion}
        editingQuestion={editingQuestion}
        translations={{
          editQuestion: t('course-content.composer.editQuestion'),
          addQuestion: t('course-content.composer.addNewQuestion'),
          questionText: t('course-content.composer.questionText'),
          questionType: t('course-content.composer.questionType'),
          answerOptions: t('course-content.composer.answerOptions'),
          correctAnswer: t('course-content.composer.correctAnswer'),
          difficulty: t('course-content.composer.difficulty'),
          tags: t('course-content.composer.tags'),
          cancel: t('course-content.composer.cancel'),
          saveChanges: t('course-content.composer.saveChanges'),
          addOption: t('course-content.composer.addOption')
        }}
      />

      {/* Question bank modal */}
      <QuestionBankModal
        isOpen={isQuestionBankModalOpen}
        onClose={() => {
          setIsQuestionBankModalOpen(false);
          setCurrentActivityId(null);
        }}
        questionBank={questionBank}
        existingQuestions={getCurrentActivity()?.questions || []}
        onSelectQuestions={handleAddQuestionsFromBank}
        translations={{
          title: t('course-content.composer.questions'),
          description: t('course-content.composer.addQuestion'),
          search: t('course-content.searchPlaceholder'),
          difficulty: t('course-content.composer.difficulty'),
          questionType: t('course-content.composer.questionType'),
          tags: t('course-content.composer.tags'),
          cancel: t('course-content.composer.cancel'),
          addSelectedQuestions: t('course-content.composer.addQuestion')
        }}
      />

      {/* Course Header */}
      <CourseHeader
        courseTitle={courseData.title || courseData.name || ""}
        courseStatus={courseData.status || ""}
        courseId={courseId}
        t={t}
      />

      {/* Content with top padding to account for fixed header */}
      <div className="mt-4">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar - Course Navigation */}
          <CourseNavigation
            modules={courseData.modules || []}
            t={t}
          />

          {/* Right area - Content Editor */}
          <div className="md:w-3/4 space-y-6 max-h-[calc(100vh-12rem)] overflow-y-auto pr-4">
            {/* Content Sections */}
            {(courseData.modules || []).length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-500 dark:text-gray-400">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    {t('course-content.detail.noModules') || 'No modules found'}
                  </h3>
                  <p className="text-sm">
                    {t('course-content.detail.noModulesDescription') || 'This course doesn\'t have any modules yet. Click "Add Module" to get started.'}
                  </p>
                </div>
              </div>
            ) : (
              (courseData.modules || []).map((module: Module, moduleIndex: number) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  moduleIndex={moduleIndex}
                  onActivityContentChange={handleActivityContentChange}
                  onOpenQuestionBank={handleOpenQuestionBank}
                  onEditQuestion={handleEditQuestion}
                  onRemoveQuestion={handleRemoveQuestion}
                  t={t}
                />
              ))
            )}

            {/* Add New Module Button */}
            <div className="flex justify-center">
              <Button variant="outline">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {t('course-content.composer.addModule')}
              </Button>
            </div>

            {/* Course Settings */}
            <CourseSettings courseData={courseData} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}
